import UserRole from 'src/types/UserRoles';

import useRole from './useRole';
import { useUser } from './useUser';

export enum UserPermissions {
  DUMMY_ENTRY = 'DUMMY_ENTRY',
  FIXED_BUY_TIME = 'FIXED_BUY_TIME',
  PACKAGE_VIEW = 'PACKAGE_VIEW',
  IRIS_RECOMMENDATION = 'IRIS_RECOMMENDATION',
  PURCHASE_AGENT = 'PURCHASE_AGENT',
  CAN_USE_CUSTOMER_MODE = 'CAN_USE_CUSTOMER_MODE',
}

interface PermissionConfig {
  users: string[];
  dealers: string[];
  adminsAllowed: boolean;
}

// me query -> user.id = users
const permissions: Record<UserPermissions, PermissionConfig> = {
  [UserPermissions.DUMMY_ENTRY]: {
    users: [],
    dealers: [
      'AT96111', // Cognify
      'AT01006', // Dummy User 5907
    ],
    adminsAllowed: false,
  },
  [UserPermissions.FIXED_BUY_TIME]: {
    users: [
      '142356', // Elli
      '142110', // Dino
      '78561', // Jakob
      'dpu3hm2', // Andi Quick
      'dp4u52w', // Dominik Malzer
      'dpogl2e', // Christian Beer (Audi)
      'dpvkfw1', // Christian Beer (VW)
    ],
    dealers: [
      'AT96111', // Cognify
    ],
    adminsAllowed: false,
  },
  [UserPermissions.IRIS_RECOMMENDATION]: {
    users: [
      '142356', // Elli Prod
      '102782', // Elli QA
      '131486', // Martina QA
      '175778', // Martina Prod
      '78561', // Jakob QA & Prod
      '136830', // Anna-Maria QA
      '182795', // Anna-Maria Prod
      '101776', // Markus Gruber POI - QA
      '121196', // Moritz Preisser POI - QA
    ],
    dealers: [
      'AT96111', // Cognify
    ],
    adminsAllowed: false,
  },
  [UserPermissions.PACKAGE_VIEW]: {
    users: [
      '157486', // Reisenhofer Prod
    ],
    dealers: [],
    adminsAllowed: true,
  },
  [UserPermissions.PURCHASE_AGENT]: {
    users: [
      'dpu3hm2', // Andi Quick
      'dpvkfw1', // Christian Beer (VW)
      'dpidbrr', // Stefan Hilpert
      'dpe7dyr', // Sören Friedling
      'dprmdy9', // Timo Reimann
      'dpcrfzw', // Nico Haker
    ],
    dealers: [],
    adminsAllowed: true,
  },
  [UserPermissions.CAN_USE_CUSTOMER_MODE]: {
    users: [
      '142356', // Elli Prod
      '102782', // Elli QA
      '131486', // Martina QA
      '175778', // Martina Prod
      '78561', // Jakob QA & Prod
      '136830', // Anna-Maria QA
      '182795', // Anna-Maria Prod
      'dpkhmkv', // Matthias Schnur - Friendly User
      'dprmdy9', // Timo Reimann - Friendly User
      'dpcrfzw', // Niko Haker - Friendly User
    ],
    dealers: [
      'AT96111', // Cognify
    ],
    adminsAllowed: false,
  },
};

export const useUserPermissions = (permission: UserPermissions): boolean => {
  const { hasRole } = useRole();
  const { data: user } = useUser();
  const userId = user?.user?.id;
  const dealerId = user?.dealer?.id;

  if (permissions[permission].adminsAllowed && hasRole(UserRole.ADMIN)) return true;
  if (dealerId && permissions[permission].dealers.includes(dealerId)) return true;
  if (userId && permissions[permission].users.includes(userId)) return true;
  if (dealerId) {
    const dealerPermission = permissions[permission].dealers.find((dealer) =>
      dealer.endsWith('*') ? dealerId.startsWith(dealer.slice(0, -1)) : dealer === dealerId,
    );
    if (dealerPermission) return true;
  }
  if (userId && permissions[permission].users.includes(userId)) return true;
  return false;
};

// Returns every permission the user/dealer has instead of checking one -- BidsTableNext needs the
// whole set to decide which columns to show.
export const useUserPermissionArray = (): UserPermissions[] => {
  const { hasRole } = useRole();
  const { data: user } = useUser();
  const userId = user?.user?.id;
  const dealerId = user?.dealer?.id;

  if (!userId && !dealerId) return [];
  return Object.entries(permissions).reduce((acc, [key, value]) => {
    if (
      (userId && value.users.includes(userId)) ||
      (value.adminsAllowed && hasRole(UserRole.ADMIN)) ||
      (dealerId && value.dealers.includes(dealerId))
    ) {
      acc.push(key as UserPermissions);
    }
    return acc;
  }, [] as UserPermissions[]);
};
